import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_URL } from '../config';
import Navbar from './Navbar';
import { Check, AlertCircle } from 'lucide-react';

const roles = ['Admin', 'User', 'Staff', 'Guard'];
const menus = [
    { key: 'dashboard', label: 'หน้าหลัก' },
    { key: 'reports', label: 'รายงาน' },
    { key: 'residents', label: 'จัดการสติ๊กเกอร์/ลูกบ้าน' },
    { key: 'rounds', label: 'จัดการรอบเวลา' },
    { key: 'zones', label: 'จัดการโซน' },
    { key: 'slots', label: 'จัดการช่องจอด' },
    { key: 'users', label: 'จัดการผู้ใช้งาน' },
    { key: 'announcements', label: 'จัดการประกาศ' },
    { key: 'roles', label: 'จัดการสิทธิ์การใช้งาน' }
];

const levels = [
    { value: 0, label: 'ซ่อน', shortLabel: 'ซ่อน', activeClass: 'bg-slate-200 text-slate-600', dotClass: 'bg-slate-400' },
    { value: 1, label: 'ดูได้', shortLabel: 'ดู', activeClass: 'bg-blue-100 text-blue-700', dotClass: 'bg-blue-500' },
    { value: 2, label: 'แก้ไขได้', shortLabel: 'แก้ไข', activeClass: 'bg-emerald-100 text-emerald-700', dotClass: 'bg-emerald-500' },
];

const RoleManagement = ({ token, role, username, setToken, permissions = [], onRefresh }) => {
    const [isSaving, setIsSaving] = useState(false);
    const [success, setSuccess] = useState('');
    const [showSuccessPopup, setShowSuccessPopup] = useState(false);
    const [showErrorPopup, setShowErrorPopup] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [localPermissions, setLocalPermissions] = useState([]);

    useEffect(() => {
        if (permissions.length > 0) {
            // Ensure every role/menu combination has an entry
            const fullPerms = [];
            roles.forEach(r => {
                menus.forEach(m => {
                    const existing = permissions.find(p => p.role === r && p.menu_key === m.key);
                    if (existing) {
                        fullPerms.push(existing);
                    } else {
                        fullPerms.push({
                            role: r,
                            menu_key: m.key,
                            is_visible: false,
                            can_edit: false
                        });
                    }
                });
            });
            setLocalPermissions(fullPerms);
        }
    }, [permissions]);

    // Get current level: 0 = ซ่อน, 1 = ดูได้, 2 = แก้ไขได้
    const getLevel = (roleName, menuKey) => {
        const perm = localPermissions.find(p => p.role === roleName && p.menu_key === menuKey);
        if (!perm || (!perm.is_visible && perm.is_visible !== 1)) return 0;
        if (perm.can_edit === 1 || perm.can_edit === true) return 2;
        return 1;
    };

    const setLevel = (roleName, menuKey, level) => {
        setLocalPermissions(prev => {
            const exists = prev.some(p => p.role === roleName && p.menu_key === menuKey);
            if (!exists) {
                return [...prev, {
                    role: roleName,
                    menu_key: menuKey,
                    is_visible: level >= 1,
                    can_edit: level >= 2
                }];
            }
            return prev.map(p => {
                if (p.role === roleName && p.menu_key === menuKey) {
                    return { ...p, is_visible: level >= 1, can_edit: level >= 2 };
                }
                return p;
            });
        });
        setSuccess('');
    };

    const handleSave = async () => {
        try {
            setErrorMessage('');
            setSuccess('');
            setIsSaving(true);
            await axios.post(`${API_URL}/api/roles/permissions`, {
                permissions: localPermissions
            }, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setSuccess('บันทึกสิทธิ์การใช้งานเรียบร้อยแล้ว');
            setShowSuccessPopup(true);
            if (onRefresh) await onRefresh();
        } catch (err) {
            console.error(err);
            setErrorMessage('เกิดข้อผิดพลาดในการบันทึกข้อมูล');
            setShowErrorPopup(true);
        } finally {
            setIsSaving(false);
        }
    };

    if (permissions.length === 0 && !isSaving) return (
        <div className="flex flex-col items-center justify-center min-h-screen bg-slate-50/50">
            <div className="w-12 h-12 border-[3px] border-slate-200 border-t-blue-600 rounded-full animate-spin mb-4 shadow-sm"></div>
            <span className="text-[12px] font-black text-slate-400 uppercase tracking-[0.3em] animate-pulse">Loading</span>
        </div>
    );

    return (
        <div className="bg-slate-50 min-h-screen pb-10">
            <Navbar role={role} username={username} onLogout={() => setToken(null)} permissions={permissions} />

            <div className="max-w-7xl mx-auto px-4 md:px-8">
                {/* Header */}
                <div className="flex items-center justify-between mb-3">
                    <div>
                        <h1 className="text-sm font-black text-slate-800 tracking-tight mt-4">จัดการสิทธิ์การใช้งาน</h1>
                        <p className="text-slate-500 text-xs font-medium">กำหนดระดับการเข้าถึงเมนูของแต่ละกลุ่มผู้ใช้งาน</p>
                    </div>
                    <button
                        onClick={handleSave}
                        disabled={isSaving}
                        className="flex items-center gap-1.5 bg-blue-600 hover:bg-blue-700 text-white px-3 py-1.5 rounded-lg font-bold text-[10px] shadow-md shadow-blue-500/10 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed border border-blue-500/20 transition-all"
                    >
                        {isSaving ? (
                            <div className="flex items-center gap-2">
                                <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></span>
                                <span>SAVING...</span>
                            </div>
                        ) : (
                            <>
                                <Check className="w-4 h-4 shrink-0" />
                                <span>บันทึก</span>
                            </>
                        )}
                    </button>
                </div>

                {/* Legend */}
                <div className="flex items-center gap-4 mb-4 px-1 flex-wrap">
                    <span className="text-[11px] font-black text-slate-400 uppercase tracking-wider">ระดับสิทธิ์:</span>
                    {levels.map(lv => (
                        <div key={lv.value} className="flex items-center gap-1.5">
                            <span className={`w-2 h-2 rounded-full ${lv.dotClass}`}></span>
                            <span className="text-xs font-semibold text-slate-500">{lv.label}</span>
                        </div>
                    ))}
                </div>

                {/* Table */}
                <div className="bg-white rounded-3xl shadow-xl shadow-slate-200/50 border border-slate-100 overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="w-full text-left border-collapse">
                            <thead>
                                <tr className="bg-slate-50/80 border-b border-slate-100">
                                    <th className="px-6 py-4 text-[11px] font-black text-slate-400 tracking-widest w-48">เมนู</th>
                                    {roles.map(r => (
                                        <th key={r} className="px-4 py-4 text-[11px] font-black text-slate-700 tracking-widest text-center">
                                            {r}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                                {menus.map((menu, idx) => (
                                    <tr
                                        key={menu.key}
                                        className={`transition-colors group ${idx % 2 === 0 ? 'bg-white' : 'bg-slate-50/40'} hover:bg-blue-50/30`}
                                    >
                                        <td className="px-6 py-4">
                                            <div className="flex flex-col">
                                                <span className="text-xs font-bold text-slate-700 group-hover:text-blue-600 transition-colors">
                                                    {menu.label}
                                                </span>
                                            </div>
                                        </td>
                                        {roles.map(r => {
                                            const currentLevel = getLevel(r, menu.key);
                                            return (
                                                <td key={r} className="px-4 py-3 text-center">
                                                    {/* 3-state segmented control */}
                                                    <div className="inline-flex rounded-lg overflow-hidden border border-slate-200 shadow-sm">
                                                        {levels.map((lv, i) => (
                                                            <button
                                                                key={lv.value}
                                                                onClick={() => setLevel(r, menu.key, lv.value)}
                                                                className={[
                                                                    'px-3 py-1.5 text-[11px] font-bold transition-all leading-none',
                                                                    i > 0 ? 'border-l border-slate-200' : '',
                                                                    currentLevel === lv.value
                                                                        ? lv.activeClass
                                                                        : 'bg-white text-slate-300 hover:text-slate-500 hover:bg-slate-50'
                                                                ].join(' ')}
                                                                title={lv.label}
                                                            >
                                                                {lv.shortLabel}
                                                            </button>
                                                        ))}
                                                    </div>
                                                </td>
                                            );
                                        })}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>

            {/* Success Popup */}
            {showSuccessPopup && (
                <div className="fixed inset-0 z-[300] flex items-center justify-center p-4 text-center">
                    <div
                        className="absolute inset-0 bg-slate-900/70 animate-in fade-in duration-300"
                        onClick={() => setShowSuccessPopup(false)}
                    />
                    <div className="bg-white rounded-[2rem] shadow-2xl w-full max-w-[320px] overflow-hidden z-20 animate-in zoom-in-95 fade-in duration-300 border border-slate-100">
                        <div className="p-10 flex flex-col items-center">
                            <div className="w-16 h-16 bg-emerald-50 rounded-full flex items-center justify-center mb-6 shadow-inner">
                                <Check className="w-8 h-8 text-emerald-500" />
                            </div>
                            <h3 className="text-xl font-black text-slate-800 tracking-tight">ทำรายการสำเร็จ</h3>
                            <p className="text-[13px] font-bold text-slate-400 mt-2 leading-relaxed">{success}</p>
                            <button
                                onClick={() => setShowSuccessPopup(false)}
                                className="mt-8 w-full py-3 bg-blue-600 text-white rounded-xl font-black text-sm shadow-lg shadow-blue-500/20 active:scale-95 transition-all"
                            >
                                ตกลง
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Error Popup */}
            {showErrorPopup && (
                <div className="fixed inset-0 z-[300] flex items-center justify-center p-4 text-center">
                    <div
                        className="absolute inset-0 bg-slate-900/70 animate-in fade-in duration-300"
                        onClick={() => setShowErrorPopup(false)}
                    />
                    <div className="bg-white rounded-[2rem] shadow-2xl w-full max-w-[340px] overflow-hidden z-20 animate-in zoom-in-95 fade-in duration-300 border border-slate-100">
                        <div className="p-10 flex flex-col items-center">
                            <div className="w-16 h-16 bg-rose-50 rounded-full flex items-center justify-center mb-6 shadow-inner">
                                <AlertCircle className="w-8 h-8 text-rose-500" />
                            </div>
                            <h3 className="text-xl font-black text-slate-800 tracking-tight">ไม่สามารถทำรายการได้</h3>
                            <p className="text-[13px] font-bold text-slate-400 mt-2 leading-relaxed whitespace-pre-wrap">{errorMessage}</p>
                            <button
                                onClick={() => setShowErrorPopup(false)}
                                className="mt-8 w-full py-3 bg-rose-500 text-white rounded-xl font-black text-sm shadow-lg shadow-rose-200 active:scale-95 transition-all"
                            >
                                เข้าใจแล้ว
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default RoleManagement;
